import type { AgentTask } from "@forgepoint/shared";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ListTodo, Paperclip } from "lucide-react";
import { MarkdownReact } from "@/components/MarkdownReact";
import { AssetDownload } from "@/components/activityEvents/AssetDownload";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import { getTaskActionDisplayName } from "./helpers/richActionHelpers";

interface AgentTaskPanelProps {
  task: AgentTask;
}

/**
 * Scrollable body for an agent-created task opened in the shared
 * `RichActionPanel`. Renders subject header, owner line, markdown-formatted
 * description body, and any attached assets.
 */
export function AgentTaskPanel({ task }: AgentTaskPanelProps) {
  const agentName = useStandardAgentDisplayName();
  const ownerLabel = formatOwnersLabel(task.owners, agentName);
  const actionLabel = task.actionParameters
    ? getTaskActionDisplayName(task.actionParameters.actionId)
    : null;
  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 p-6">
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
            <ListTodo className="h-4 w-4 text-muted-foreground" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-base font-semibold leading-snug">
                {task.subject ?? "(no subject)"}
              </h2>
              {actionLabel && (
                <span className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {actionLabel}
                </span>
              )}
            </div>
            {ownerLabel && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                Assignee: {ownerLabel}
              </p>
            )}
          </div>
        </div>
      </div>
      <Separator />
      <div className="flex-1 overflow-auto p-6">
        {task.description ? (
          <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5">
            <MarkdownReact>{task.description}</MarkdownReact>
          </div>
        ) : (
          <p className="text-sm italic text-muted-foreground">
            No description provided.
          </p>
        )}
      </div>
      {task.assetIds.length > 0 && (
        <>
          <Separator />
          <Collapsible defaultOpen={false} className="shrink-0">
            <CollapsibleTrigger className="group flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-muted/50">
              <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <Paperclip className="h-3 w-3" aria-hidden />
                {task.assetIds.length === 1
                  ? "1 attachment"
                  : `${task.assetIds.length} attachments`}
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
              <div className="flex max-h-80 flex-col gap-1 overflow-y-auto px-6 pb-6">
                {task.assetIds.map((assetId) => (
                  <AssetDownload key={assetId} assetId={assetId} />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}
    </div>
  );
}

function formatOwnersLabel(
  owners: AgentTask["owners"],
  agentName: string,
): string | null {
  if (owners.length === 0) {
    return null;
  }
  const labels = owners
    .map((owner) => {
      if (owner.type === "agent") {
        return agentName;
      }
      const name = [owner.firstName, owner.lastName].filter(Boolean).join(" ");
      return name || owner.email || null;
    })
    .filter((label): label is string => label !== null);
  if (labels.length === 0) {
    return null;
  }
  if (labels.length <= 2) {
    return labels.join(", ");
  }
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}
